import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { CurrentUserService, IOnboardingMetadata, IOnboardingMetadataEntry } from "@gtmhub/users";
import { SettingsVisibilityService } from "@webapp/navigation/services/settings/settings-visibility.service";
import {
  closeJourneyState,
  completeJourneyState,
  createInitialOnboardingState,
  disabledJourneyState,
  journeyFirstStep,
} from "@webapp/shared/components/onboarding/onboarding-state.util";
import { IOnboardingJourneyState, KPI_COMMENTS_USER_SETTINGS_KEY } from "@webapp/shared/components/onboarding/onboarding.models";
import dayjs from "@webapp/shared/libs/dayjs";

@Injectable()
export class KpiCommentsOnboardingService {
  private onboardingMetadata: IOnboardingMetadataEntry;
  private markedForCompletion = false;
  private totalSteps = 1;
  private onboardingState$: BehaviorSubject<IOnboardingJourneyState> = new BehaviorSubject<IOnboardingJourneyState>(createInitialOnboardingState(this.totalSteps));
  private skipSteps: number[] = [];

  constructor(private currentUserService: CurrentUserService, private settingsVisibilityService: SettingsVisibilityService) {
    // setting default value of the onboarding metadata for the specific journey
    const onboardingMetaData = {
      [KPI_COMMENTS_USER_SETTINGS_KEY]: { timesShown: 0, isCompleted: false, lastWatchedDate: null },
      ...this.currentUserService.getUserSetting<IOnboardingMetadata>("onboardingMetadata"),
    };

    this.onboardingMetadata = onboardingMetaData[KPI_COMMENTS_USER_SETTINGS_KEY];
    this.settingsVisibilityService.showSettings$.subscribe((showSettings: boolean) => {
      if (showSettings && this.shouldSeeJourneyAccordingToMetadata()) {
        this.updateState(journeyFirstStep(this.totalSteps, this.skipSteps));
      } else {
        const currentStep = 1;
        this.updateState(disabledJourneyState(this.totalSteps, currentStep));
      }
    });
  }

  public getState$(): Observable<IOnboardingJourneyState> {
    return this.onboardingState$.asObservable();
  }

  public close(): void {
    this.updateState(closeJourneyState(this.onboardingState$.value));
  }

  public complete(): void {
    this.markedForCompletion = true;
    this.onboardingMetadata.isCompleted = true;
    this.syncOnboardingMetadata();
    this.close();
  }

  public markAsShown(): void {
    this.onboardingMetadata.timesShown++;
    this.onboardingMetadata.lastWatchedDate = dayjs().utc().toISOString();
    this.onboardingMetadata.isCompleted = this.onboardingMetadata.timesShown >= 3;
    this.syncOnboardingMetadata();
  }

  private syncOnboardingMetadata(): void {
    this.currentUserService.setUserSetting({
      onboardingMetadata: {
        ...this.currentUserService.getUserSetting<IOnboardingMetadata>("onboardingMetadata"),
        [KPI_COMMENTS_USER_SETTINGS_KEY]: this.onboardingMetadata,
      },
    });
  }

  public shouldSeeJourneyAccordingToMetadata(): boolean {
    const isCompleted = this.onboardingMetadata.isCompleted;
    const exceededMaxViews = this.onboardingMetadata.timesShown >= 3;
    const lastWatchedWithin24hours = dayjs().diff(this.onboardingMetadata.lastWatchedDate, "hours") <= 24;

    if (isCompleted || exceededMaxViews || lastWatchedWithin24hours) {
      return false;
    }

    return true;
  }

  private updateState(state: IOnboardingJourneyState): void {
    if (this.markedForCompletion) {
      this.onboardingState$.next(completeJourneyState(state));
      this.markedForCompletion = false;
    } else {
      this.onboardingState$.next(state);
    }
  }
}
